import type { FC } from 'react'
import { LoadingIndicator } from '../LoadingIndicator/LoadingIndicator'
import type { VehiclePlanCalculationDataResponse } from '../../types/charging-steering.types'
import UpdateIcon from '../../assets/images/update_rg_b_48_def.png'
import { formatAllowedChargingPower } from '../../features/chargingSteering/utils/chargingPlan.utils'

type CalculationDataPanelProps = {
  calculationData?: VehiclePlanCalculationDataResponse
  loading: boolean
  onRefresh: () => void
}

export const CalculationDataPanel: FC<CalculationDataPanelProps> = ({
  calculationData,
  loading,
  onRefresh,
}) => {
  const formatSocAsPercent = (soc?: number): string => {
    if (typeof soc !== 'number' || Number.isNaN(soc)) {
      return '-'
    }

    const normalized = soc <= 1 ? soc * 100 : soc
    return `${normalized.toFixed(2).replace(/\.00$/, '')}%`
  }

  const formatDateTime = (value?: string): string => {
    if (!value) {
      return '-'
    }

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
      return '-'
    }

    return date.toLocaleString()
  }

  return (
    <section className="charging-plan-editor-wrapper">
      <div className="calculation-data-header">
        <h2>Calculation Data</h2>
        <button
          type="button"
          className="calculation-refresh-button"
          aria-label="Refresh Calculation Data"
          onClick={onRefresh}
          disabled={loading}
        >
          {loading ? (
            <LoadingIndicator style="INLINE" />
          ) : (
            <img src={UpdateIcon} alt="" className="calculation-refresh-icon" />
          )}
        </button>
      </div>
      {calculationData ? (
        <div className="calculation-data-grid">
          <p>
            <strong>Session start:</strong> {formatDateTime(calculationData.sessionStartTime)}
          </p>
          <p>
            <strong>Max plan end:</strong> {formatDateTime(calculationData.maxPlanEndTime)}
          </p>
          <p>
            <strong>Total energy required:</strong> {calculationData.totalEnergyRequired ?? '-'} Wh
          </p>
          <p>
            <strong>Allowed charging power:</strong>{' '}
            {formatAllowedChargingPower(calculationData.allowedChargingPower)}
          </p>
          <p>
            <strong>Start SoC:</strong> {formatSocAsPercent(calculationData.startSoC)}
          </p>
          <p>
            <strong>Target SoC:</strong> {formatSocAsPercent(calculationData.targetSoC)}
          </p>
          <p>
            <strong>Step limit:</strong> {calculationData.stepLimit ?? '-'}
          </p>
          <p>
            <strong>Available pauses:</strong> {calculationData.availablePauses ?? '-'}
          </p>
          <p>
            <strong>Renegotiation budget:</strong> {calculationData.renegotiationBudget ?? '-'}
          </p>
          <p>
            <strong>Renegotiable:</strong> {calculationData.isRenegotiable ? 'Yes' : 'No'}
          </p>
        </div>
      ) : (
        <p>No calculation data loaded yet.</p>
      )}
    </section>
  )
}
